import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Droplets, Sprout, Leaf, ArrowRight, ShoppingCart, Star, TrendingUp } from 'lucide-react';
import { getProducts } from '../services/api';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    const res = await getProducts();
    if (res.success) {
      // Get first 3 products as featured
      setFeaturedProducts(res.data.slice(0, 3));
    }
  };

  const benefits = [
    {
      icon: <Droplets className="w-8 h-8" />,
      title: 'Water Conservation',
      description: 'Save up to 60% water compared to traditional irrigation methods'
    },
    {
      icon: <Sprout className="w-8 h-8" />,
      title: 'Increased Crop Yield',
      description: 'Boost your farm productivity with efficient water delivery'
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Sustainable Agriculture',
      description: 'Environment-friendly solutions for modern farming'
    }
  ];

  const stats = [
    { icon: <Star className="w-6 h-6" />, value: '1 Million+', label: 'Happy Customers' },
    { icon: <TrendingUp className="w-6 h-6" />, value: '15+', label: 'Years Experience' },
    { icon: <Droplets className="w-6 h-6" />, value: '60%', label: 'Water Saved' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Animation */}
      <section
        className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16 relative overflow-hidden"
        style={{ background: 'var(--gradient-hero)' }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="heading-1 mb-6 animate-fade-in-up">
            Modern Irrigation Solutions for <span style={{ color: 'var(--accent-text)' }}>Sustainable Farming</span>
          </h1>
          <p className="body-large mb-8 animate-fade-in-up" style={{ color: 'var(--text-secondary)', animationDelay: '0.2s' }}>
            Leading provider of drip irrigation systems and agriculture solutions in Dharmapuri, Tamil Nadu.
            Helping farmers improve crop yield while conserving water.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <a href="tel:6382799541" className="btn-primary group">
              <Phone className="w-5 h-5 group-hover:animate-bounce" />
              Call Now
            </a>
            <Link to="/shop" className="btn-secondary group">
              <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
              Shop Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4" style={{ background: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-center mb-3 text-primary">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Staggered Animation */}
      <section className="py-20 px-4" style={{ background: 'var(--bg-page)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="heading-2 mb-4">Why Choose SEELAKARIYMMAN IRRIGATION</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Your trusted partner for agriculture and irrigation solutions since our establishment.
              We are committed to supporting farmers with quality products and expert guidance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="product-card text-center hover:scale-105 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex justify-center mb-4 text-primary transform hover:rotate-12 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="heading-3 mb-3" style={{ fontSize: '1.25rem' }}>{benefit.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 px-4" style={{ background: 'var(--bg-section)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="heading-2 mb-4">Featured Products</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Explore our top-selling irrigation solutions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {featuredProducts.map((product, index) => (
              <Link
                key={product.id}
                to={`/product/${product.slug}`}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    ₹{product.price.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{product.category}</span>
                    <span className="text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/shop" className="btn-primary group">
              <ShoppingCart className="w-5 h-5" />
              Shop All Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section with Gradient */}
      <section className="py-20 px-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-strong) 100%)' }}>
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10 animate-fade-in-up">
          <h2 className="heading-2 mb-4 text-white">Ready to Transform Your Farm?</h2>
          <p className="body-large mb-8 text-white/90">
            Contact us today for expert consultation and quality irrigation solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6382799541" className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105">
              <Phone className="w-5 h-5" />
              Call: 6382799541
            </a>
            <Link to="/contact" className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105">
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;